import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles,
  Menu,
  X,
  PlayCircle,
  Zap,
  Trophy,
  Target,
  Smartphone,
  FileText,
  TrendingDown,
  Eye,
  ShieldCheck,
  ArrowRight,
  Search,
  Layers,
  ClipboardCheck,
  Crown,
  Check,
  Star,
  Quote,
  User2,
  Instagram,
  Linkedin,
  Building2,
  HeartHandshake,
  Users,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_EMAIL = "hello@flashspecs.app";
const SOCIALS = {
  instagram: "https://instagram.com/flashspecs",
  tiktok: "https://tiktok.com/@flashspecs",
  linkedin: "https://linkedin.com/company/flashspecs",
};

// Inline TikTok icon (lucide-react does not include brand icons)
const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52V6.94a4.85 4.85 0 0 1-1.84-.25Z" />
  </svg>
);

// ──────────────────────────────────────────────────────────────
// LandingPage — FlashSpecs public marketing site
// Palette: bg #0F1C14 · primary #2E4A3E · gold #C9A227 · cream #F5F0E6
// ──────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [audience, setAudience] = useState("individual");
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);

  // If the user is already logged in, push them to their dashboard
  // — unless they explicitly want a preview (?preview=1).
  // Per Emergent Auth playbook: skip when returning from OAuth callback (hash has session_id);
  // AuthCallback handles the exchange first.
  useEffect(() => {
    const isPreview = new URLSearchParams(window.location.search).get("preview") === "1";
    if (isPreview) return;
    if (window.location.hash?.includes("session_id=")) return;

    // If we don't even have a token in localStorage AND no cookie exists,
    // skip the API call entirely — saves a network round-trip and avoids
    // a stale 401 toast on a brand-new visitor.
    const hasLocalToken = !!localStorage.getItem("session_token");
    const hasSessionCookie = document.cookie.split(";").some((c) => c.trim().startsWith("session_token="));
    if (!hasLocalToken && !hasSessionCookie) return;

    axios
      .get(`${API}/auth/me`)
      .then((res) => {
        const role = res.data?.role;
        if (!res.data) return;
        if (["admin", "org_admin", "venue_manager", "system_admin"].includes(role)) navigate("/dashboard", { replace: true });
        else navigate("/dashboard", { replace: true });
      })
      .catch((err) => {
        // 401 → session is genuinely gone, clean up local stale token.
        // Network/transient errors → keep token; user can retry from login.
        if (err?.response?.status === 401) {
          localStorage.removeItem("session_token");
        }
      });
  }, [navigate]);

  useEffect(() => {
    const onScroll = () => setShowFloatingCTA(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleWaitlist = async (e, source = "landing_final_cta") => {
    e?.preventDefault?.();
    if (!email.trim()) {
      toast.error("Pop your email in first.");
      return;
    }
    const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!emailRe.test(email.trim())) {
      toast.error("That email looks a bit off. Try again?");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        name: name.trim() || null,
        audience,
        source,
      });
      setJoined(true);
      if (res.data?.already_registered) toast.success("You're already on the list — we'll be in touch soon.");
      else toast.success("You're in. Early access coming your way.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileNavOpen(false);
  };

  const venueMailto = `mailto:${CONTACT_EMAIL}?subject=Venue%20enquiry%20%E2%80%94%20FlashSpecs%20demo&body=Hi%2C%20I%20run%20a%20venue%20and%20I%27d%20like%20to%20learn%20more%20about%20FlashSpecs.%0A%0AVenue%20name%3A%20%0ALocation%3A%20%0ATeam%20size%3A%20%0A%0AThanks%21`;

  return (
    <div className="fs-landing min-h-screen" data-testid="landing-page">
      <StyleTag />

      {/* ─────────── Navbar ─────────── */}
      <nav className="fs-nav" data-testid="landing-nav">
        <div className="fs-container flex items-center justify-between py-5">
          <a href="#top" className="flex items-center gap-2 group" data-testid="landing-logo">
            <Sparkles size={20} className="text-[#C9A227] transition-transform group-hover:rotate-12" />
            <span className="text-lg font-semibold tracking-tight text-[#C9A227]" style={{ fontFamily: "var(--fs-serif)" }}>
              FlashSpecs
            </span>
          </a>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollTo("how")} className="fs-link" data-testid="nav-how">How it Works</button>
            <button onClick={() => scrollTo("for-venues")} className="fs-link" data-testid="nav-venues">For Venues</button>
            <button onClick={() => scrollTo("for-individuals")} className="fs-link" data-testid="nav-enthusiasts">For Enthusiasts</button>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="fs-link text-sm" data-testid="nav-login">Login</Link>
            <button onClick={() => scrollTo("cta")} className="fs-btn-gold text-sm" data-testid="nav-join-waitlist">
              Join Waitlist
            </button>
          </div>
          <button className="md:hidden text-[#F5F0E6]" onClick={() => setMobileNavOpen(true)} data-testid="mobile-nav-toggle" aria-label="Open menu">
            <Menu size={22} />
          </button>
        </div>
      </nav>

      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 bg-[#0F1C14]/98 backdrop-blur-md md:hidden" data-testid="mobile-nav-menu">
          <div className="flex justify-end p-6">
            <button onClick={() => setMobileNavOpen(false)} className="text-[#F5F0E6]" aria-label="Close menu"><X size={24} /></button>
          </div>
          <div className="flex flex-col items-center gap-6 mt-10 text-lg">
            <button onClick={() => scrollTo("how")} className="fs-link">How it Works</button>
            <button onClick={() => scrollTo("for-venues")} className="fs-link">For Venues</button>
            <button onClick={() => scrollTo("for-individuals")} className="fs-link">For Enthusiasts</button>
            <Link to="/login" onClick={() => setMobileNavOpen(false)} className="fs-link">Login</Link>
            <button onClick={() => scrollTo("cta")} className="fs-btn-gold">Join Waitlist</button>
          </div>
        </div>
      )}

      {/* ─────────── Hero ─────────── */}
      <section id="top" className="fs-hero" data-testid="hero-section">
        <div className="fs-container max-w-3xl text-center pt-12 lg:pt-20 pb-5 sm:pb-6 space-y-7">
          <div className="fs-pill mx-auto" data-testid="hero-pill">
            <Sparkles size={12} className="text-[#C9A227]" />
            Early Access Now Open
          </div>
          <h1 className="fs-h1" data-testid="hero-headline">
            Master cocktails, wine &amp; food <span className="text-[#C9A227] italic">the fun way.</span>
          </h1>
          <p className="fs-lede fs-shout mx-auto" data-testid="hero-sub">
            REPLACE BORING PDFS WITH ADDICTIVE, GAMIFIED TRAINING THAT STAFF ACTUALLY ENJOY.
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <a href={venueMailto} className="fs-btn-gold text-base" data-testid="hero-cta-venue">
              <Mail size={16} className="mr-2" /> For Venues – Get in Touch
            </a>
            <button onClick={() => scrollTo("cta")} className="fs-btn-gold-outline text-base" data-testid="hero-cta-primary">
              Join the Waitlist <ArrowRight size={16} className="ml-1.5" />
            </button>
          </div>
          <div className="flex items-center justify-center gap-6 pt-2 text-xs text-[#F5F0E6]/60">
            <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#C9A227]" /> No card required</span>
            <span className="flex items-center gap-1.5"><Check size={14} className="text-[#C9A227]" /> Built in Wales</span>
            <button type="button" onClick={() => setDemoOpen(true)} className="hidden sm:flex items-center gap-1.5 text-[#F5F0E6]/60 hover:text-[#C9A227] transition-colors" data-testid="hero-cta-demo">
              <PlayCircle size={14} className="text-[#C9A227]" /> Watch 60-second demo
            </button>
          </div>
        </div>
      </section>

      {/* ─────────── Who This Is For ─────────── */}
      <section className="fs-section fs-section-alt" data-testid="who-section">
        <div className="fs-container max-w-6xl">
          <div className="fs-section-head">
            <span className="fs-kicker">Who this is for</span>
            <h2 className="fs-h2 mt-3 text-center">Built for the Industry and the Obsessed.</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-6 mt-14">
            <div id="for-venues" className="fs-world-card text-center" data-testid="who-venues">
              <div className="fs-icon-circle-lg mx-auto"><Building2 size={28} className="text-[#C9A227]" strokeWidth={1.6} /></div>
              <span className="fs-kicker mt-6 inline-block">For hospitality venues</span>
              <h3 className="fs-h2-sm mt-3">PERFECT FOR BARS, RESTAURANTS &amp; HOTELS</h3>
              <p className="fs-body mt-4 max-w-md mx-auto">Train your staff with engaging, modern tools. Reduce onboarding time and improve consistency across every shift.</p>
              <a href={venueMailto} className="fs-btn-gold mt-8 inline-flex" data-testid="who-venues-cta">
                Partner With Us – Get a Demo <ArrowRight size={15} className="ml-1.5" />
              </a>
            </div>
            <div id="for-individuals" className="fs-world-card text-center" data-testid="who-individuals">
              <div className="fs-icon-circle-lg mx-auto"><HeartHandshake size={28} className="text-[#C9A227]" strokeWidth={1.6} /></div>
              <span className="fs-kicker mt-6 inline-block">For the obsessed</span>
              <h3 className="fs-h2-sm mt-3">FOR FOODIES, COCKTAIL &amp; WINE LOVERS</h3>
              <p className="fs-body mt-4 max-w-md mx-auto">Obsessed with wine, cocktails, or home cooking? Geek out with beautiful flashcards and quizzes that make learning genuinely fun.</p>
              <button onClick={() => scrollTo("cta")} className="fs-btn-gold mt-8" data-testid="who-individuals-cta">
                Join the Waitlist <ArrowRight size={15} className="ml-1.5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── The Problem ─────────── */}
      <section className="fs-section" data-testid="problem-section">
        <div className="fs-container max-w-5xl">
          <h2 className="fs-h2 text-center" data-testid="problem-headline">
            Hospitality training is broken — <span className="text-[#C9A227]">and it's costing you money.</span>
          </h2>
          <p className="fs-body mt-5 max-w-3xl mx-auto text-center">
            Staff are still learning from static PDFs, scattered notes and outdated portals. The result? Inconsistent service, slow onboarding, frustrated managers and high turnover.
          </p>
          <div className="grid sm:grid-cols-3 gap-5 mt-14">
            {[
              { icon: FileText, t: "BORING PDFS NOBODY READS", d: "Specs live in 20 tabs and a Dropbox folder from 2019." },
              { icon: TrendingDown, t: "ZERO ENGAGEMENT OR ACCOUNTABILITY", d: "You train them Monday. By Friday it's gone." },
              { icon: Eye, t: "NO WAY TO TRACK REAL PROGRESS", d: "Who actually knows the menu? You're guessing." },
            ].map((p, i) => (
              <div key={i} className="fs-pain-card text-center" data-testid={`pain-card-${i}`}>
                <div className="fs-icon-circle-lg mx-auto"><p.icon size={28} className="text-[#C9A227]" strokeWidth={1.6} /></div>
                <h3 className="fs-h3-lg mt-6">{p.t}</h3>
                <p className="fs-body-step mt-3">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── The Solution ─────────── */}
      <section className="fs-section fs-section-alt" data-testid="solution-section">
        <div className="fs-container max-w-5xl">
          <h2 className="fs-h2 text-center" data-testid="solution-headline">
            FlashSpecs turns training into <span className="text-[#C9A227] italic">something people actually want to do.</span>
          </h2>
          <p className="fs-body mt-5 max-w-3xl mx-auto text-center">
            Beautiful flashcards. Smart quizzes. Real-time leaderboards. Built specifically for bartenders, chefs and hospitality teams.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
            {[
              { icon: Layers, t: "STUNNING VISUAL FLASHCARDS", d: "With exact measurements, garnish, glassware and method." },
              { icon: Trophy, t: "GAMIFIED QUIZZES", d: "Healthy competition. Real XP. Venue-wide leaderboards." },
              { icon: Target, t: "INSTANT PERFORMANCE TRACKING", d: "Spot who knows what — no guesswork." },
              { icon: Smartphone, t: "WORKS ON YOUR PHONE", d: "Train on the bus, on break, before service." },
            ].map((b, i) => (
              <div key={i} className="fs-benefit-card text-center" data-testid={`benefit-card-${i}`}>
                <div className="fs-icon-gold-lg mx-auto"><b.icon size={24} strokeWidth={1.7} /></div>
                <h3 className="fs-h3-lg mt-6">{b.t}</h3>
                <p className="fs-body-step mt-3">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── How It Works ─────────── */}
      <section id="how" className="fs-section" data-testid="how-section">
        <div className="fs-container max-w-5xl">
          <div className="fs-section-head">
            <span className="fs-kicker">Simple, engaging training that actually sticks.</span>
            <h2 className="fs-h2 mt-3 text-center">How it works</h2>
            <p className="fs-body mt-5 max-w-2xl mx-auto text-center">Four steps from new-starter to service-ready.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
            {[
              { icon: Search, t: "BROWSE OR SEARCH", d: "Beautiful decks for cocktails, wine, food, HACCP and allergens — organised the way bartenders think." },
              { icon: Layers, t: "STUDY WITH FLASHCARDS", d: "Interactive, swipeable flashcards. Spaced repetition brings back what you got wrong." },
              { icon: ClipboardCheck, t: "TEST YOURSELF", d: "Smart, adaptive quizzes that target your weak areas — not the same five questions everyone knows." },
              { icon: Crown, t: "CLIMB THE LEADERBOARD", d: "Track real improvement venue-wide, company-wide, or just you vs. last week." },
            ].map((s, i) => (
              <div key={i} className="fs-step-card text-center" data-testid={`step-${i}`}>
                <div className="fs-icon-gold-lg mx-auto"><s.icon size={24} strokeWidth={1.7} /></div>
                <h3 className="fs-h3-lg mt-6">{s.t}</h3>
                <p className="fs-body-step mt-3">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── See It In Action ─────────── */}
      <section className="fs-section fs-section-alt" data-testid="proof-section">
        <div className="fs-container max-w-6xl">
          <h2 className="fs-h2 text-center" data-testid="proof-headline">
            This is what real training <span className="text-[#C9A227] italic">looks like in 2026.</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
            {[
              { label: "Browse & search" },
              { label: "Cocktail detail" },
              { label: "Smart quiz" },
              { label: "Leaderboard" },
            ].map((s, i) => (
              <figure key={i} className="fs-screenshot-placeholder" data-testid={`screenshot-${i}`}>
                <div className="aspect-[9/16] w-full flex flex-col items-center justify-center gap-3 p-6">
                  <div className="fs-icon-gold mx-auto"><Smartphone size={22} /></div>
                  <p className="text-[10px] uppercase tracking-[.25em] text-[#F5F0E6]/40">Placeholder</p>
                  <p className="fs-small text-center">Screenshot coming soon</p>
                </div>
                <figcaption className="text-center pb-4 fs-small-muted">{s.label} · Real UI from the current build</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── Social Proof ─────────── */}
      <section className="fs-section" data-testid="social-proof-section">
        <div className="fs-container max-w-6xl">
          <h2 className="fs-h2 text-center">
            Already loved by the <span className="text-[#C9A227] italic">people who matter most.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {[
              { q: "This is 10× better than any training portal I've used in 12 years.", a: "Senior Bartender", l: "Cardiff" },
              { q: "My team actually asks to use it. That's never happened before.", a: "Bar Manager", l: "Swansea" },
              { q: "Finally, something that makes learning specs enjoyable instead of painful.", a: "Mixologist", l: "Newport" },
            ].map((t, i) => (
              <blockquote key={i} className="fs-testimonial text-center" data-testid={`testimonial-${i}`}>
                <div className="fs-stars justify-center" aria-label="5 out of 5 stars">
                  {[0,1,2,3,4].map(k => <Star key={k} size={14} fill="#C9A227" stroke="#C9A227" />)}
                </div>
                <Quote size={18} className="text-[#C9A227] mx-auto mt-4 mb-3" />
                <p className="fs-quote-text">"{t.q}"</p>
                <footer className="mt-6 flex items-center justify-center gap-3">
                  <div className="fs-avatar"><User2 size={14} /></div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-[#F5F0E6]">{t.a}</p>
                    <p className="text-xs text-[#F5F0E6]/50">{t.l}</p>
                  </div>
                </footer>
              </blockquote>
            ))}
          </div>
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="stats-bar">
            {[
              { n: "500+", l: "flashcards" },
              { n: "95%+", l: "completion in testing" },
              { n: "4.8★", l: "average rating" },
              { n: "20 yrs", l: "hospitality experience behind it" },
            ].map((s, i) => (
              <div key={i} className="fs-stat fs-stat-lg">
                <p className="fs-stat-num">{s.n}</p>
                <p className="fs-stat-lbl">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── Founder Story ─────────── */}
      <section className="fs-section fs-section-alt" data-testid="founder-section">
        <div className="fs-container max-w-5xl">
          <div className="grid sm:grid-cols-[260px_1fr] gap-10 sm:gap-14 items-center">
            <div className="fs-founder-photo" aria-label="Founder photo placeholder">
              <User2 size={64} className="text-[#C9A227]/70" />
              <span className="fs-small-muted absolute bottom-4">Photo coming soon</span>
            </div>
            <div className="text-center sm:text-left">
              <span className="fs-kicker">Built by someone who's actually done the job.</span>
              <blockquote className="fs-founder-quote mt-4">
                "After almost 20 years working in bars, restaurants and events, I got tired of watching talented staff struggle with outdated training tools. So I built the platform I wish I had."
              </blockquote>
              <p className="fs-founder-attrib mt-5">— <span className="text-[#C9A227]">Wayne O'Neill</span>, Founder · Wales, UK</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── Get Involved ─────────── */}
      <section id="get-involved" className="fs-section" data-testid="get-involved-section">
        <div className="fs-container max-w-6xl">
          <div className="fs-section-head">
            <h2 className="fs-h2 text-center" data-testid="get-involved-headline">
              Let's build this <span className="text-[#C9A227] italic">together.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-14">
            <div className="fs-world-card text-center" data-testid="involve-venues">
              <div className="fs-icon-circle-lg mx-auto"><Building2 size={28} className="text-[#C9A227]" strokeWidth={1.6} /></div>
              <h3 className="fs-h3-lg mt-6">FOR VENUES</h3>
              <p className="fs-body-step mt-3 max-w-sm mx-auto">Interested in trialling FlashSpecs with your team? We'd love to hear from you.</p>
              <a href={venueMailto} className="fs-btn-gold mt-7 inline-flex" data-testid="involve-venues-cta">
                More Information <ArrowRight size={14} className="ml-1.5" />
              </a>
            </div>
            <div className="fs-world-card text-center" data-testid="involve-individuals">
              <div className="fs-icon-circle-lg mx-auto"><Users size={28} className="text-[#C9A227]" strokeWidth={1.6} /></div>
              <h3 className="fs-h3-lg mt-6">FOR INDIVIDUALS</h3>
              <p className="fs-body-step mt-3 max-w-sm mx-auto">Want early access when we launch? Join the waitlist and we'll send you in first.</p>
              <button onClick={() => scrollTo("cta")} className="fs-btn-gold mt-7" data-testid="involve-individuals-cta">
                Join Waitlist <ArrowRight size={14} className="ml-1.5" />
              </button>
            </div>
            <div className="fs-world-card text-center" data-testid="involve-follow">
              <div className="fs-icon-circle-lg mx-auto"><Sparkles size={26} className="text-[#C9A227]" strokeWidth={1.6} /></div>
              <h3 className="fs-h3-lg mt-6">FOLLOW US</h3>
              <p className="fs-body-step mt-3 max-w-sm mx-auto">Stay updated on progress, new features &amp; launch announcements.</p>
              <div className="flex justify-center gap-3 mt-7" data-testid="involve-socials">
                <a href={SOCIALS.instagram} target="_blank" rel="noreferrer" className="fs-social-btn" data-testid="social-instagram" aria-label="Instagram">
                  <Instagram size={18} />
                </a>
                <a href={SOCIALS.tiktok} target="_blank" rel="noreferrer" className="fs-social-btn" data-testid="social-tiktok" aria-label="TikTok">
                  <TikTokIcon size={17} />
                </a>
                <a href={SOCIALS.linkedin} target="_blank" rel="noreferrer" className="fs-social-btn" data-testid="social-linkedin" aria-label="LinkedIn">
                  <Linkedin size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── Final CTA ─────────── */}
      <section id="cta" className="fs-section fs-cta fs-section-alt" data-testid="final-cta-section">
        <div className="fs-container max-w-3xl text-center">
          <h2 className="fs-h1-sm" data-testid="cta-headline">
            Be among the first to <span className="text-[#C9A227] italic">experience FlashSpecs.</span>
          </h2>
          <p className="fs-body mt-5">
            We're preparing to launch soon. Join the waitlist or get in touch if you run a venue.
          </p>
          <p className="fs-small mt-2 italic text-[#F5F0E6]/60">Master cocktails, wine &amp; food the fun way.</p>

          {joined ? (
            <div className="fs-success-card mt-10" data-testid="waitlist-success">
              <div className="fs-icon-gold-lg mx-auto"><Check size={26} /></div>
              <h3 className="fs-h3-lg mt-5">YOU'RE ON THE LIST</h3>
              <p className="fs-body-step mt-3">We'll be in touch the moment your spot opens up.</p>
              <a href={venueMailto} className="fs-btn-ghost mt-6 inline-flex text-sm" data-testid="success-venue-cta">
                <Mail size={14} className="mr-2" /> Run a venue? Let's talk
              </a>
            </div>
          ) : (
            <>
              <form onSubmit={(e) => handleWaitlist(e, "landing_final_cta")} className="mt-10 grid gap-3 max-w-xl mx-auto" data-testid="waitlist-form">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input
                    type="text"
                    placeholder="Your name (optional)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="fs-input"
                    data-testid="waitlist-name-input"
                    aria-label="Name"
                  />
                  <Input
                    type="text"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="fs-input"
                    data-testid="waitlist-email-input"
                    aria-label="Email"
                  />
                </div>
                <select
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  className="fs-select"
                  data-testid="waitlist-audience"
                  aria-label="I am a"
                >
                  <option value="individual">I'm an Individual (bartender / chef / enthusiast)</option>
                  <option value="venue">I run a Venue (bar / restaurant / hotel)</option>
                  <option value="both">Both — I run a venue AND want personal access</option>
                </select>
                <div className="grid sm:grid-cols-2 gap-3 mt-2">
                  <a href={venueMailto} className="fs-btn-gold w-full" data-testid="cta-venue">
                    I run a venue – Let's Talk
                  </a>
                  <button type="submit" className="fs-btn-gold-outline w-full" disabled={submitting} data-testid="waitlist-submit">
                    {submitting ? "Securing…" : "Join the Waitlist / Newsletter"}
                  </button>
                </div>
              </form>
              <p className="fs-urgency mt-6" data-testid="cta-urgency">
                We're preparing to launch soon. Early access is limited.
              </p>
            </>
          )}
          <p className="fs-small-muted mt-4">No spam. Unsubscribe anytime. We respect your inbox.</p>
        </div>
      </section>

      {/* ─────────── Footer ─────────── */}
      <footer className="fs-footer" data-testid="landing-footer">
        <div className="fs-container py-10 flex flex-col sm:flex-row items-center justify-between gap-5">
          <p className="text-xs text-[#F5F0E6]/50">© 2026 FlashSpecs · Wales, UK</p>
          <div className="flex items-center gap-5 text-xs text-[#F5F0E6]/60">
            <a href="#" className="fs-link">Privacy</a>
            <a href={`mailto:${CONTACT_EMAIL}`} className="fs-link">Contact</a>
            <div className="flex items-center gap-3">
              <a href={SOCIALS.instagram} target="_blank" rel="noreferrer" className="fs-link" aria-label="Instagram" data-testid="footer-instagram"><Instagram size={14} /></a>
              <a href={SOCIALS.tiktok} target="_blank" rel="noreferrer" className="fs-link" aria-label="TikTok" data-testid="footer-tiktok"><TikTokIcon size={14} /></a>
              <a href={SOCIALS.linkedin} target="_blank" rel="noreferrer" className="fs-link" aria-label="LinkedIn" data-testid="footer-linkedin"><Linkedin size={14} /></a>
            </div>
          </div>
          <p className="text-xs text-[#F5F0E6]/50 italic">Built in Wales. Designed for the world.</p>
        </div>
      </footer>

      {showFloatingCTA && (
        <div className="fs-float-cta md:hidden" data-testid="floating-cta">
          <button onClick={() => scrollTo("cta")} className="fs-btn-gold w-full">
            Join Waitlist · Free Early Access
          </button>
        </div>
      )}

      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent className="bg-[#13251C] border-[#2E4A3E] text-[#F5F0E6] max-w-md" data-testid="demo-modal">
          <DialogHeader>
            <DialogTitle className="text-[#C9A227]" style={{ fontFamily: "var(--fs-serif)" }}>Demo coming soon</DialogTitle>
            <DialogDescription className="text-[#F5F0E6]/70">
              We're putting the finishing touches on the 60-second walkthrough. Join the waitlist and we'll send it to your inbox first.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setDemoOpen(false)} className="text-[#F5F0E6]/80 hover:bg-[#2E4A3E]/40" data-testid="demo-close">Close</Button>
            <button onClick={() => { setDemoOpen(false); scrollTo("cta"); }} className="fs-btn-gold text-sm" data-testid="demo-join">Notify me</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Inline styles
function StyleTag() {
  return (
    <style>{`
      :root {
        --fs-bg: #0F1C14;
        --fs-bg-2: #13251C;
        --fs-primary: #2E4A3E;
        --fs-primary-2: #3B5E50;
        --fs-gold: #C9A227;
        --fs-gold-soft: rgba(201,162,39,.14);
        --fs-cream: #F5F0E6;
        --fs-cream-dim: rgba(245,240,230,.72);
        --fs-sans: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
        --fs-serif: 'Playfair Display', 'Cormorant Garamond', Georgia, serif;
      }
      .fs-landing {
        background: var(--fs-bg);
        color: var(--fs-cream);
        font-family: var(--fs-sans);
        -webkit-font-smoothing: antialiased;
        position: relative;
      }
      .fs-landing::before {
        content: '';
        position: fixed; inset: 0;
        background:
          radial-gradient(1000px 500px at 80% -10%, rgba(46,74,62,.55), transparent 60%),
          radial-gradient(700px 400px at -10% 30%, rgba(201,162,39,.06), transparent 65%);
        pointer-events: none; z-index: 0;
      }
      .fs-landing > * { position: relative; z-index: 1; }
      .fs-container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
      @media (min-width: 768px) { .fs-container { padding: 0 40px; } }

      /* Nav */
      .fs-nav { position: sticky; top: 0; z-index: 40; backdrop-filter: blur(16px); background: rgba(15,28,20,.72); border-bottom: 1px solid rgba(245,240,230,.06); }
      .fs-link { color: rgba(245,240,230,.75); font-size: 14px; transition: color .2s ease; background: none; border: none; cursor: pointer; padding: 0; display: inline-flex; align-items: center; }
      .fs-link:hover { color: var(--fs-gold); }

      /* Buttons */
      .fs-btn-gold {
        display: inline-flex; align-items: center; justify-content: center;
        background: var(--fs-gold); color: #0F1C14;
        padding: 12px 22px; border-radius: 999px;
        font-weight: 600; font-size: 14px; letter-spacing: .01em;
        border: 1px solid var(--fs-gold);
        transition: transform .2s ease, box-shadow .2s ease, background .2s ease;
        box-shadow: 0 6px 24px -10px rgba(201,162,39,.5);
        cursor: pointer; white-space: nowrap;
      }
      .fs-btn-gold:hover { transform: translateY(-1px); background: #d6b13a; box-shadow: 0 10px 30px -10px rgba(201,162,39,.7); }
      .fs-btn-gold:active { transform: translateY(0); }
      .fs-btn-gold:disabled { opacity: .65; cursor: not-allowed; transform: none; }

      .fs-btn-gold-outline {
        display: inline-flex; align-items: center; justify-content: center;
        background: transparent; color: var(--fs-gold);
        padding: 11px 22px; border-radius: 999px;
        font-weight: 600; font-size: 14px;
        border: 1px solid var(--fs-gold);
        transition: all .2s ease;
        cursor: pointer; white-space: nowrap;
      }
      .fs-btn-gold-outline:hover { background: var(--fs-gold-soft); transform: translateY(-1px); }

      .fs-btn-ghost {
        display: inline-flex; align-items: center; justify-content: center;
        background: transparent; color: var(--fs-cream);
        padding: 11px 22px; border-radius: 999px;
        font-weight: 500; font-size: 14px;
        border: 1px solid rgba(245,240,230,.22);
        transition: all .2s ease;
        cursor: pointer;
      }
      .fs-btn-ghost:hover { border-color: var(--fs-gold); color: var(--fs-gold); background: rgba(201,162,39,.06); }

      /* Hero */
      .fs-hero { padding-top: 32px; }
      .fs-pill {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 6px 14px; border-radius: 999px;
        background: var(--fs-gold-soft); color: var(--fs-gold);
        font-size: 11px; letter-spacing: .12em; text-transform: uppercase; font-weight: 700;
        border: 1px solid rgba(201,162,39,.28);
      }
      .fs-h1 {
        font-family: var(--fs-serif);
        font-weight: 700; line-height: 1.02; letter-spacing: -0.02em;
        font-size: clamp(40px, 6vw, 72px);
        color: var(--fs-cream);
      }
      .fs-h1-sm {
        font-family: var(--fs-serif);
        font-weight: 700; line-height: 1.05; letter-spacing: -0.02em;
        font-size: clamp(32px, 4.5vw, 52px);
        color: var(--fs-cream);
      }
      .fs-h2 {
        font-family: var(--fs-serif);
        font-weight: 700; line-height: 1.1; letter-spacing: -0.015em;
        font-size: clamp(28px, 3.6vw, 42px);
        color: var(--fs-cream);
      }
      .fs-h2-sm { font-family: var(--fs-serif); font-weight: 700; font-size: clamp(20px, 2.2vw, 26px); color: var(--fs-cream); letter-spacing: .03em; line-height: 1.25; }
      .fs-h3 { font-size: 16px; font-weight: 600; color: var(--fs-cream); letter-spacing: -.005em; }
      .fs-h3-lg { font-size: clamp(15px, 1.2vw, 17px); font-weight: 700; color: var(--fs-cream); letter-spacing: .14em; line-height: 1.4; text-transform: uppercase; }
      .fs-lede { font-size: clamp(16px, 1.35vw, 19px); line-height: 1.55; color: var(--fs-cream-dim); max-width: 600px; }
      .fs-shout { font-weight: 700; letter-spacing: .08em; color: var(--fs-cream); }
      .fs-body { font-size: 15px; line-height: 1.65; color: var(--fs-cream-dim); }
      .fs-body-step { font-size: 14.5px; line-height: 1.6; color: var(--fs-cream-dim); font-weight: 400; }
      .fs-quote-text { font-size: clamp(16px, 1.4vw, 19px); line-height: 1.55; color: var(--fs-cream); font-weight: 500; letter-spacing: -.005em; }
      .fs-small { font-size: 13.5px; line-height: 1.55; color: rgba(245,240,230,.62); }
      .fs-small-muted { font-size: 12px; color: rgba(245,240,230,.45); letter-spacing: .02em; }
      .fs-kicker { font-size: 11px; letter-spacing: .22em; text-transform: uppercase; font-weight: 700; color: var(--fs-gold); display: inline-block; }
      .fs-urgency { font-size: 13.5px; color: rgba(245,240,230,.62); letter-spacing: .01em; }

      /* Section heads centred */
      .fs-section-head { text-align: center; }

      /* Sections */
      .fs-section { padding: 110px 0; }
      @media (max-width: 768px) { .fs-section { padding: 80px 0; } }
      .fs-section-alt {
        background:
          linear-gradient(180deg, rgba(46,74,62,.18), rgba(46,74,62,.05));
        border-top: 1px solid rgba(245,240,230,.05);
        border-bottom: 1px solid rgba(245,240,230,.05);
      }

      /* Cards */
      .fs-pain-card, .fs-benefit-card, .fs-world-card, .fs-testimonial, .fs-step-card {
        background: linear-gradient(180deg, rgba(46,74,62,.22), rgba(46,74,62,.08));
        border: 1px solid rgba(245,240,230,.07);
        border-radius: 18px; padding: 32px 24px;
        transition: transform .3s ease, border-color .3s ease, box-shadow .3s ease;
      }
      .fs-pain-card:hover, .fs-benefit-card:hover, .fs-world-card:hover, .fs-testimonial:hover, .fs-step-card:hover {
        transform: translateY(-4px);
        border-color: rgba(201,162,39,.35);
        box-shadow: 0 16px 44px -22px rgba(201,162,39,.4);
      }
      .fs-world-card { padding: 40px 32px; }

      /* Icon shapes */
      .fs-icon-circle {
        width: 42px; height: 42px; border-radius: 50%;
        background: var(--fs-gold-soft);
        display: inline-flex; align-items: center; justify-content: center;
        border: 1px solid rgba(201,162,39,.28);
      }
      .fs-icon-circle-lg {
        width: 72px; height: 72px; border-radius: 50%;
        background: linear-gradient(135deg, rgba(201,162,39,.22), rgba(201,162,39,.05));
        display: flex; align-items: center; justify-content: center;
        border: 1.5px solid rgba(201,162,39,.45);
        box-shadow: 0 10px 28px -12px rgba(201,162,39,.55);
      }
      .fs-icon-gold {
        width: 44px; height: 44px; border-radius: 12px;
        background: linear-gradient(135deg, rgba(201,162,39,.25), rgba(201,162,39,.08));
        display: inline-flex; align-items: center; justify-content: center;
        color: var(--fs-gold);
        border: 1px solid rgba(201,162,39,.3);
      }
      .fs-icon-gold-lg {
        width: 64px; height: 64px; border-radius: 18px;
        background: linear-gradient(135deg, rgba(201,162,39,.28), rgba(201,162,39,.08));
        display: flex; align-items: center; justify-content: center;
        color: var(--fs-gold);
        border: 1.5px solid rgba(201,162,39,.4);
        box-shadow: 0 10px 28px -14px rgba(201,162,39,.55);
      }

      /* Screenshots */
      .fs-screenshot-placeholder {
        background: linear-gradient(180deg, rgba(46,74,62,.3), rgba(15,28,20,.4));
        border: 1px solid rgba(245,240,230,.08);
        border-radius: 22px; overflow: hidden;
        transition: transform .3s ease, border-color .3s ease, box-shadow .3s ease;
      }
      .fs-screenshot-placeholder:hover { transform: translateY(-4px); border-color: rgba(201,162,39,.4); box-shadow: 0 20px 50px -25px rgba(201,162,39,.4); }

      /* Hero cocktail card */
      .fs-hero-card {
        position: relative;
        background: linear-gradient(160deg, rgba(46,74,62,.6), rgba(15,28,20,.85));
        border: 1px solid rgba(201,162,39,.3);
        border-radius: 28px; padding: 2px;
        box-shadow: 0 40px 100px -30px rgba(0,0,0,.75), 0 0 80px -20px rgba(201,162,39,.32);
        animation: fsFloat 6s ease-in-out infinite;
        max-width: 640px; width: 100%; margin-left: auto;
      }
      .fs-hero-card-inner { padding: 40px; border-radius: 26px; background: rgba(15,28,20,.4); backdrop-filter: blur(12px); }
      @media (max-width: 768px) { .fs-hero-card-inner { padding: 28px; } }
      .fs-hero-card-chip {
        display: inline-flex; align-items: center; gap: 6px;
        background: var(--fs-gold-soft); color: var(--fs-gold);
        padding: 5px 12px; border-radius: 999px;
        font-size: 11px; letter-spacing: .14em; text-transform: uppercase; font-weight: 700;
        border: 1px solid rgba(201,162,39,.3);
      }
      .fs-hero-card-title { font-size: clamp(28px, 2.6vw, 38px); line-height: 1.1; margin-top: 16px; font-weight: 700; color: var(--fs-cream); letter-spacing: -.015em; }
      .fs-hero-divider { height: 1px; background: linear-gradient(to right, transparent, rgba(201,162,39,.4), transparent); margin: 24px 0; }
      .fs-hero-spec { display: flex; flex-direction: column; gap: 12px; }
      .fs-hero-row { display: grid; grid-template-columns: 90px 1fr; gap: 16px; align-items: baseline; font-size: 15px; }
      .fs-hero-measure { color: var(--fs-gold); font-weight: 600; font-variant-numeric: tabular-nums; letter-spacing: .02em; }
      .fs-hero-ingredient { color: var(--fs-cream-dim); }
      @keyframes fsFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }

      /* Testimonials */
      .fs-avatar { width: 34px; height: 34px; border-radius: 50%; background: var(--fs-gold-soft); border: 1px solid rgba(201,162,39,.3); display: inline-flex; align-items: center; justify-content: center; color: var(--fs-gold); }
      .fs-stars { display: inline-flex; gap: 3px; }

      /* Stats */
      .fs-stat { text-align: center; padding: 20px 12px; border-radius: 14px; background: rgba(46,74,62,.18); border: 1px solid rgba(245,240,230,.06); }
      .fs-stat-lg { padding: 30px 14px; border-radius: 18px; background: linear-gradient(180deg, rgba(46,74,62,.3), rgba(46,74,62,.1)); border-color: rgba(201,162,39,.18); }
      .fs-stat-num { font-family: var(--fs-serif); font-weight: 700; font-size: clamp(34px, 4vw, 48px); color: var(--fs-gold); letter-spacing: -.02em; line-height: 1; }
      .fs-stat-lbl { font-size: 13px; color: rgba(245,240,230,.65); margin-top: 10px; letter-spacing: .03em; font-weight: 500; }

      /* Founder */
      .fs-founder-photo {
        width: 100%; max-width: 260px; aspect-ratio: 1/1;
        border-radius: 24px;
        background: linear-gradient(160deg, rgba(46,74,62,.4), rgba(15,28,20,.6));
        border: 1px dashed rgba(201,162,39,.4);
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        position: relative;
        margin: 0 auto;
      }
      .fs-founder-quote { font-family: var(--fs-serif); font-size: clamp(20px, 1.8vw, 26px); line-height: 1.5; color: var(--fs-cream); font-style: italic; letter-spacing: -.005em; }
      .fs-founder-attrib { font-size: 14px; color: var(--fs-cream-dim); font-weight: 500; letter-spacing: .01em; }

      /* Social buttons */
      .fs-social-btn {
        width: 42px; height: 42px; border-radius: 50%;
        display: inline-flex; align-items: center; justify-content: center;
        background: var(--fs-gold-soft); color: var(--fs-gold);
        border: 1px solid rgba(201,162,39,.35);
        transition: all .2s ease;
      }
      .fs-social-btn:hover { background: var(--fs-gold); color: #0F1C14; transform: translateY(-2px); box-shadow: 0 10px 24px -10px rgba(201,162,39,.6); }

      /* CTA */
      .fs-cta { position: relative; overflow: hidden; }
      .fs-cta::before {
        content: ''; position: absolute; inset: 0;
        background: radial-gradient(700px 300px at 50% 0%, rgba(201,162,39,.12), transparent 60%);
        pointer-events: none;
      }
      .fs-input {
        background: rgba(15,28,20,.6) !important;
        border: 1px solid rgba(245,240,230,.15) !important;
        color: var(--fs-cream) !important;
        height: 48px !important; padding: 0 18px !important;
        border-radius: 999px !important; font-size: 15px !important;
      }
      .fs-input:focus { border-color: var(--fs-gold) !important; box-shadow: 0 0 0 3px rgba(201,162,39,.18) !important; outline: none !important; }
      .fs-select {
        background: rgba(15,28,20,.6); border: 1px solid rgba(245,240,230,.15);
        color: var(--fs-cream); height: 48px; padding: 0 18px; border-radius: 999px;
        font-size: 14.5px; font-family: var(--fs-sans); width: 100%;
        appearance: none;
        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23C9A227' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>");
        background-repeat: no-repeat;
        background-position: right 18px center;
        padding-right: 44px;
      }
      .fs-select:focus { border-color: var(--fs-gold); box-shadow: 0 0 0 3px rgba(201,162,39,.18); outline: none; }
      .fs-select option { background: #0F1C14; color: var(--fs-cream); }

      .fs-success-card {
        margin-top: 40px; padding: 40px 32px; border-radius: 22px;
        background: linear-gradient(180deg, rgba(46,74,62,.4), rgba(46,74,62,.15));
        border: 1px solid rgba(201,162,39,.45);
      }

      /* Footer */
      .fs-footer { border-top: 1px solid rgba(245,240,230,.06); }

      /* Floating CTA */
      .fs-float-cta {
        position: fixed; bottom: 16px; left: 16px; right: 16px; z-index: 30;
        animation: fsSlideUp .4s ease-out;
      }
      @keyframes fsSlideUp { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

      .fs-landing + #emergent-badge, #emergent-badge { display: none !important; }
    `}</style>
  );
}
